import { Children, isValidElement, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useLocale } from "../i18n";

/** Shared horizontal section navigation. Children retain their own navigation and selection semantics. */
export function ScrollTabs({ children, label, className, activeKey }: { children: ReactNode; label: string; className: string; activeKey?: string }) {
  const { w, lang } = useLocale();
  const itemsKey = Children.toArray(children).map(child => isValidElement(child) ? child.key : "").join("|");
  const navigation = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const node = navigation.current;
    if (!node) return;
    const measure = () => {
      const box = node.getBoundingClientRect();
      const buttons = Array.from(node.children, child => child.getBoundingClientRect());
      const next = { left: buttons.some(b => b.left < box.left - 1), right: buttons.some(b => b.right > box.right + 1) };
      setEdges(previous => previous.left === next.left && previous.right === next.right ? previous : next);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    for (const child of node.children) observer.observe(child);
    node.addEventListener("scroll", measure, { passive: true });
    measure();
    return () => { observer.disconnect(); node.removeEventListener("scroll", measure); };
  }, [lang, itemsKey]);
  useEffect(() => {
    const node = navigation.current, active = node?.querySelector('[aria-current="page"], [aria-pressed="true"]');
    if (!node || !active) return;
    const box = node.getBoundingClientRect(), tab = active.getBoundingClientRect();
    const offset = tab.left < box.left ? tab.left - box.left : tab.right > box.right ? tab.right - box.right : 0;
    if (offset) node.scrollBy({ left: offset, behavior: "instant" });
  }, [activeKey, lang, itemsKey]);
  const scroll = (direction: number) => navigation.current?.scrollBy({ left: direction * navigation.current.clientWidth * 0.75, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
  return <div className={`scroll-tabs ${className}-shell`} data-scroll-left={edges.left} data-scroll-right={edges.right}>
    <button type="button" className="scroll-tabs-arrow scroll-tabs-left" hidden={!edges.left && !edges.right} disabled={!edges.left} aria-label={w("تمرير الأقسام إلى اليسار", "Scroll sections left")} onClick={() => scroll(-1)}><ChevronLeftIcon aria-hidden="true" /></button>
    <nav ref={navigation} dir={lang === "ar" ? "rtl" : "ltr"} className={`scroll-tabs-list ${className}`} aria-label={label}>{children}</nav>
    <button type="button" className="scroll-tabs-arrow scroll-tabs-right" hidden={!edges.left && !edges.right} disabled={!edges.right} aria-label={w("تمرير الأقسام إلى اليمين", "Scroll sections right")} onClick={() => scroll(1)}><ChevronRightIcon aria-hidden="true" /></button>
  </div>;
}
